import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { round } from 'utils';

dayjs.extend(utc);
dayjs.extend(timezone);

export const STATISTIC_ENTRY_TYPES = {
  TypeNameDownload: 'Downloading data',
  TypeNameDataProcessing: 'Data processing',
  TypeNameTotalTime: 'Total time',
} as const;

export const STATISTIC_NAME = 'POS-Brazil';

export const MS_LOG_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSSSSZ';

export interface Statistic {
  timestamp: number;
  date: string;
  description: string;
  itemType: typeof STATISTIC_ENTRY_TYPES[keyof typeof STATISTIC_ENTRY_TYPES];
  record?: {
    duration: number;
    durationMeasure: 'ms' | 's';
    error?: string;
  };
  recordNumber: number;
  name: string;
}

export class Statistics {
  private data: Statistic[] = [];

  private id: number = Date.now();

  getStatistic(description: string): Statistic | null {
    return this.data.find(entry => entry.description === description) || null;
  }

  getAllStatistics(): Statistic[] {
    return this.data;
  }

  getEntry() {
    return {
      id: this.id,
      statistics: this.data,
    };
  }

  init(value: Pick<Statistic, 'itemType' | 'description'>): void {
    window.performance.mark(`${this.id}-${value.description}`);
    const now = Date.now();
    const logEntry: Statistic = {
      ...value,
      name: STATISTIC_NAME,
      date: dayjs(now).format(MS_LOG_DATE_FORMAT),
      timestamp: now,
      recordNumber: this.id,
    };
    this.data.push(logEntry);
  }

  finishLog(description: string, error?: string): void {
    const item = this.data.find(entry => entry.description === description);
    const descriptionForPerformance = !error
      ? description
      : `${description} (info: ${error})`;
    if (item) {
      window.performance.measure(
        `${descriptionForPerformance}-${this.id}-${description}`,
        `${this.id}-${description}`,
      );
      const newRecord: Statistic['record'] = {
        duration: Number(round((Date.now() - item.timestamp) / 1000, 2)),
        durationMeasure: 's',
      };
      if (error) {
        newRecord.error = error;
      }
      item.record = newRecord;
      delete item.timestamp;
    } else {
      // eslint-disable-next-line no-console
      console.error(
        `Failed to finish Statistic log - statistic with name ${description} was not initialized`,
      );
    }
  }

  clearAll(): void {
    this.data = [];
    this.id = Date.now();
  }

  /*
    Checks if there's a statistic type for a Total time with a duration and returns it
    Otherwise returns undefined
  */
  getFullDurationStat(): Statistic | undefined {
    const fullData = this.data;
    const fullDuration = fullData.find(
      stat => stat.itemType === STATISTIC_ENTRY_TYPES.TypeNameTotalTime,
    );

    return fullDuration;
  }
}
